<template>
  <div class="viewer-app">
    <!-- Channel page -->
    <ChannelPage
      v-if="channelHandle"
      :handle="channelHandle"
      :injected-branding="customDomainBranding"
      :api-url="apiUrl"
    />
    <!-- Tour viewer -->
    <TourViewer
      v-else-if="tourId"
      :tour-id="tourId"
      :api-url="apiUrl"
      :storage-url="storageUrl"
      :initial-theme="initialTheme"
      :allow-theme-change="true"
      :preview-token="previewToken"
      @tour-loaded="onTourLoaded"
    />
    <div v-else class="error-page">
      <div class="error-card">
        <div class="error-logo">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="#3b82f6" stroke-width="1.5"/>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="#3b82f6"/>
          </svg>
        </div>
        <h1 class="error-title">VirtualTourEasy</h1>
        <div class="error-divider"></div>
        <p class="error-text">No tour specified</p>
        <p class="error-hint">Please check the URL or contact the tour owner for the correct link.</p>
        <a href="https://virtualtoureasy.com" class="error-link" target="_blank">
          Visit VirtualTourEasy.com →
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import TourViewer from './components/TourViewer.vue'
import ChannelPage from './components/ChannelPage.vue'
import { themes } from './themes'
import { useI18n } from 'vue-i18n'
import { setTourMeta } from './composables/useSeoMeta'

const { t } = useI18n()

// Get URL parameters
const tourId = ref<string | null>(null)
const previewToken = ref<string | null>(null)
const customDomainBranding = ref<any>(null) // branding injected when on a custom domain
const channelHandle = ref<string | null>(null)
const initialTheme = ref<string | null>(null)

// Get environment variables
const apiUrl = import.meta.env.VITE_API_URL || 'https://api-dev.virtualtoureasy.com'
// authUrl removed — viewer uses public /viewer/:id endpoint, no auth needed
const storageUrl = import.meta.env.VITE_STORAGE_URL || 'https://storage.virtualtoureasy.com'

onMounted(async () => {
  const params = new URLSearchParams(window.location.search)
  const path = window.location.pathname

  // Check for channel route: /c/handle
  const channelMatch = path.match(/^\/c\/([a-z0-9][a-z0-9-]{1,39})$/i)
  if (channelMatch) {
    channelHandle.value = channelMatch[1].toLowerCase()
    return
  }

  // Get tour ID — support query params (?v=, ?tourid=) or path-based (/TOUR_ID)
  // Matches UUIDs (hex + dashes, 36 chars) AND legacy alphanumeric IDs (e.g. "AmdxNzZC2nDroe1flup7")
  const pathMatch = path.match(/^\/([a-zA-Z0-9_-]{8,80})$/)
  tourId.value = params.get('tourid') || params.get('v') || (pathMatch ? pathMatch[1] : null)
  previewToken.value = params.get('preview_token') || null

  // ── Custom domain detection ────────────────────────────────────────────────
  // If the viewer is loaded from a non-VTE hostname (e.g. tours.customer.com),
  // resolve the domain to the org's channel handle and branding via the API,
  // then redirect to /c/<channel_handle> so the channel page renders.
  const hostname = window.location.hostname
  const isVteDomain = hostname === 'localhost' ||
    hostname.includes('127.0.0.1') ||
    hostname.endsWith('.virtualtoureasy.com') ||
    hostname.endsWith('.pages.dev')

  if (!isVteDomain && !tourId.value && !channelHandle.value) {
    try {
      const res = await fetch(`${apiUrl}/api/branding/domain/${encodeURIComponent(hostname)}`)
      if (res.ok) {
        const data = await res.json() as { branding: any; channel_handle: string | null }
        customDomainBranding.value = data.branding
        if (data.channel_handle) {
          channelHandle.value = data.channel_handle
        }
      }
    } catch { /* silently ignore — falls through to normal tour/error flow */ }
  }
  // ─────────────────────────────────────────────────────────────────────────
  
  // Get theme if valid
  const themeParam = params.get('theme')
  if (themeParam && themes[themeParam]) {
    initialTheme.value = themeParam
  }
})

function onTourLoaded(meta: { title: string; description: string; image: string; orgName: string | null }) {
  setTourMeta({
    title: meta.title || null,
    description: meta.description || null,
    image: meta.image || null,
    url: window.location.href,
    orgName: meta.orgName
  })
}
</script>

<style>
:root {
  --background-color: #1e293b;
  --text-color: #f8fafc;
  --error-color: #ef4444;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  height: 100%;
  height: 100dvh;
  overflow: hidden;
  -webkit-overflow-scrolling: touch;
}

body {
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu,
    Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  background-color: var(--background-color);
  color: var(--text-color);
}

#app {
  height: 100%;
  height: 100dvh;
  overflow: hidden;
}

.viewer-app {
  width: 100%;
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height — accounts for mobile browser chrome */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.error-page {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
}

.error-card {
  text-align: center;
  max-width: 420px;
  padding: 3rem 2.5rem;
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(148, 163, 184, 0.15);
  border-radius: 16px;
  backdrop-filter: blur(12px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.error-logo {
  margin-bottom: 1rem;
}

.error-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #f8fafc;
  margin-bottom: 1.25rem;
  letter-spacing: -0.02em;
}

.error-divider {
  width: 48px;
  height: 3px;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  border-radius: 2px;
  margin: 0 auto 1.5rem;
}

.error-text {
  color: #e2e8f0;
  font-size: 1.125rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
}

.error-hint {
  color: #94a3b8;
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 2rem;
}

.error-link {
  display: inline-block;
  padding: 0.625rem 1.5rem;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
}

.error-link:hover {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}
</style>
